import { Button } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { IntegrationResponse } from '@/api';
import { adapterName } from '@/components/Integrations/adapters/copy';
import {
  capabilitiesFor,
  failureText,
  useIntegrationCapabilities,
} from '@/components/Integrations/capabilities';
import { DataTable } from '@/components/Ui/DataTable';
import { EmptyText } from '@/components/Ui/EmptyText';
import { Page } from '@/components/Ui/Page';
import { PageHeader } from '@/components/Ui/PageHeader';
import { UiLink } from '@/components/Ui/UiLink';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { relativeTime } from '@/lib/relative-time';
import { useRustyState } from '@/state';
import classes from '../AccountScreens.module.css';
import {
  failureStatus,
  integrationTarget,
  isFailedOutcome,
  usedByTitles,
} from './integration-text';

export function IntegrationsListScreen() {
  const { t, i18n } = useTranslation();
  const { createLanguageURL } = useLanguageAwareRouting();
  const caps = useIntegrationCapabilities();
  const [rows, setRows] = useState<IntegrationResponse[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    useRustyState
      .getState()
      .api.getAllIntegrations()
      .then((all) => {
        if (live) setRows(all);
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, []);

  const lastDelivery = (row: IntegrationResponse) => {
    const last = row.last_delivery;
    const failed7d = row.failed_7d ?? 0;
    const status = failureStatus(row);
    const http = status ? t('integrations.index.http', { status }) : '';
    if (failed7d > 0 || (last && isFailedOutcome(last.outcome))) {
      return (
        <span className={classes.riskText}>
          <span className={classes.riskDot} aria-hidden="true" />
          {failed7d > 0
            ? t('integrations.index.failed7d', { count: failed7d })
            : t('integrations.index.lastFailed')}
          {http}
        </span>
      );
    }
    if (!last) return <span className={classes.sub}>{t('integrations.index.never')}</span>;
    return (
      <span>
        {t('integrations.index.delivered', { when: relativeTime(last.created_at, i18n.language) })}
      </span>
    );
  };

  const usedBy = (row: IntegrationResponse) => {
    if (row.form_count === undefined) return <span className={classes.sub}>–</span>;
    if (row.form_count === 0) {
      return (
        <span className={classes.stackTight}>
          <span>{t('integrations.index.noForms')}</span>
          <span className={classes.help}>{t('integrations.index.noFormsHint')}</span>
        </span>
      );
    }
    return (
      <span className={classes.stackTight}>
        <span>{t('integrations.index.formCount', { count: row.form_count })}</span>
        <span className={classes.help}>{usedByTitles(row.form_titles, row.form_count, t)}</span>
      </span>
    );
  };

  return (
    <Page width="wide">
      <PageHeader
        title={t('integrations.pageTitle')}
        subtitle={t('integrations.index.subtitle')}
        actions={
          <Button component={Link} to={createLanguageURL('/account/integrations/new')}>
            {t('integrations.index.new')}
          </Button>
        }
      />

      {failed ? (
        <p className={classes.errorText}>{t('integrations.index.loadFailed')}</p>
      ) : rows === null ? (
        <EmptyText>{t('ui.loading')}</EmptyText>
      ) : (
        <div className={classes.tableCard}>
          <DataTable
            caption={t('integrations.pageTitle')}
            rows={rows}
            rowKey={(r) => r.id}
            empty={t('integrations.index.empty')}
            columns={[
              {
                key: 'name',
                header: t('integrations.index.colIntegration'),
                render: (r) => {
                  const c = capabilitiesFor(caps, r.kind);
                  const target = r.kind === 'email' ? integrationTarget(r, t) : '';
                  return (
                    <span className={classes.stackTight}>
                      <UiLink to={`/account/integrations/${r.id}/deliveries`}>
                        <strong>{r.title}</strong>
                      </UiLink>
                      <span className={classes.help}>
                        {adapterName(r.kind, t)}
                        {target ? ` · ${target}` : ''}
                        {(r.signed ?? c.signed) ? ` · ${t('integrations.index.signed')}` : ''}
                      </span>
                    </span>
                  );
                },
              },
              { key: 'used', header: t('integrations.index.colUsedBy'), render: usedBy },
              { key: 'last', header: t('integrations.index.colLast'), render: lastDelivery },
              {
                key: 'fails',
                header: t('integrations.index.colFails'),
                render: (r) => (
                  <span className={classes.sub}>
                    {failureText(capabilitiesFor(caps, r.kind), t)}
                  </span>
                ),
              },
              {
                key: 'actions',
                header: (
                  <span className={classes.visuallyHidden}>
                    {t('integrations.index.colActions')}
                  </span>
                ),
                render: (r) => (
                  <span className={classes.nowrap}>
                    <Button
                      component={Link}
                      to={createLanguageURL(`/account/integrations/${r.id}/deliveries`)}
                      variant="subtle"
                      size="xs"
                    >
                      {t('integrations.index.deliveries')}
                    </Button>
                    <Button
                      component={Link}
                      to={createLanguageURL(`/account/integrations/${r.id}`)}
                      variant="subtle"
                      size="xs"
                      aria-label={t('integrations.index.editLabel', { title: r.title })}
                    >
                      {t('ui.edit')}
                    </Button>
                  </span>
                ),
              },
            ]}
          />
        </div>
      )}

      <p className={classes.help} style={{ margin: 0 }}>
        {t('integrations.index.cost')}
      </p>
    </Page>
  );
}
